import { defineStore } from 'pinia'
import { logoutStripeConnectInstances } from '@/services/stripeConnectSession.js'

const virtualUsernameDomain = (import.meta.env.VITE_GROUPREV_USERNAMES_VIRTUAL_DOMAIN || '').replace(/^@+/, '')

function normalizeLoginEmail(email) {
  const value = `${email || ''}`.trim()

  if (value === '' || value.includes('@')) {
    return value
  }

  if (virtualUsernameDomain === '') {
    return value
  }

  return `${value}@${virtualUsernameDomain}`
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    previous_user: null,
    intended_url: null,
  }),
  actions: {
    set_user(user) {
      this.user = user
    },
    set_intended_url(intendedUrl) {
      this.intended_url = intendedUrl
    },
    async attempt_user() {
      const currentUserId = this.user?.id

      try {
        const { data } = await axios.get('/api/v1/me')

        if(currentUserId && currentUserId !== data.current_user?.id) {
          await logoutStripeConnectInstances()
        }

        this.user = data.current_user
        this.previous_user = data.previous_user || null
        return data.current_user
      }
      catch(e) {
        if(currentUserId) {
          await logoutStripeConnectInstances()
        }

        this.user = null
      }
    },
    async logout() {
      try {
        const response = await axios.post('/logout')
        await logoutStripeConnectInstances()
        await this.router.push('/')
        this.$reset()

        return response
      } catch (error) {
        console.error(error)
        throw error
      }
    },
    async loginViaToken(token) {
      await axios.get('/sanctum/csrf-cookie');
      const loginResponse = await axios.post('/api/login-via-token', {token:token})
      if (loginResponse?.status === 200 && loginResponse.data?.two_factor) {
        await this.router.push({ name: 'TwoFactorChallenge' })
        return
      }
      await logoutStripeConnectInstances()
      const payload = await this.attempt_user()
      if (this.user && payload.redirect_to) {
        await this.router.push(payload.redirect_to)
      }
    },
    async login(payload, noRedirect) {
      await axios.get('/sanctum/csrf-cookie');
      const loginPayload = {
        ...payload,
        email: normalizeLoginEmail(payload?.email),
      }
      const loginResponse = await axios.post('/login', loginPayload)

      if (loginResponse?.status === 200 && loginResponse.data?.two_factor) {
        await this.router.push({ name: 'TwoFactorChallenge' })
        return
      }
      await logoutStripeConnectInstances()
      await this.attempt_user()
      if (this.user && loginPayload.redirect_to) {
        await this.router.push(loginPayload.redirect_to)
      }
      else if(this.user && this.intended_url) {
        console.log('redirecting to intended url', this.intended_url)
        await this.router.push(this.intended_url)
        this.intended_url = null
      }
      else if(this.user && !noRedirect) {
        await this.router.push({ name: 'DashboardPage' })
      }
    },
    async startImpersonation(userId) {
      const { data } = await axios.post(`/api/v1/impersonate/${userId}`,{
        source_url: this.router.currentRoute.value.fullPath
      })
      await logoutStripeConnectInstances()
      this.user = data.user
      this.previous_user = data.previous_user || null
      await this.attempt_user()
    },
    async stopImpersonation() {
      const { data } = await axios.post(`/api/v1/impersonate-stop`)
      await logoutStripeConnectInstances()
      this.previous_user = null
      this.user = data.user
      if(data.original_url_source) {
        await this.router.push(data.original_url_source)
      }
    }
  },
  getters: {
    currentUser: (state) => state.user,
    previousUser: (state) => state.previous_user,
    hasOrganizationAccess: (state) => {
      if (state.user?.admin) {
        return true
      }

      if (!state.user) {
        return false
      }

      const memberships = state.user.organization_memberships || []

      return (state.user.organization_permissions || []).length > 0
        || memberships.some((membership) => (membership.permissions || []).length > 0)
    },
    can: (state) => (permission, organizationId = null) => {
      if (state.user?.admin) {
        return true
      }

      if (!state.user || !permission) {
        return false
      }

      const memberships = state.user.organization_memberships || []

      if (organizationId !== null && organizationId !== undefined) {
        const normalizedOrganizationId = Number(organizationId)

        return memberships.some((membership) => (
          Number(membership.organization_id) === normalizedOrganizationId
          && (membership.permissions || []).includes(permission)
        ))
      }

      return (state.user.organization_permissions || []).includes(permission)
        || memberships.some((membership) => (membership.permissions || []).includes(permission))
    },
  },
})
